import type { StructuredSearchStore } from "../stores/structured-search/structured-search-store";
import type { FilterDefinition } from "../config/structured-search-schema";
import { getVersionedEntityConfig } from "../config/versioned-entity-config";
import {
  ANY_VERSION_VALUE,
  createVersionedEntityFilterValue,
  formatVersionedEntityDisplayLabel,
} from "../config/versioned-entity-utils";

import type { OperatorSuggestionItem, SuggestionItem, ValueSuggestionItem } from "./types";

interface SelectSuggestionOptions {
  nestedKeyMode: boolean;
  isPromptField: boolean;
  isTraceVersionedEntityField: boolean;
  versionStep: boolean;
}

export const selectSuggestion = (
  store: StructuredSearchStore,
  suggestion: SuggestionItem,
  options: SelectSuggestionOptions,
) => {
  const { nestedKeyMode, isPromptField, isTraceVersionedEntityField, versionStep } = options;

  if (store.step === "field") {
    const field = suggestion as FilterDefinition;
    store.selectField(field.key, field.type);
    return;
  }

  if (store.step === "operator") {
    const operator = suggestion as OperatorSuggestionItem;
    store.selectOperator(operator.operator);
    return;
  }

  if (store.step === "key" && nestedKeyMode) {
    const value = suggestion as ValueSuggestionItem;
    store.selectMetadataKey(value.value, value.label);
    return;
  }

  if (store.step !== "value") {
    return;
  }

  const value = suggestion as ValueSuggestionItem;
  if (nestedKeyMode) {
    store.setNestedKey(value.value, value.label);
    store.setInputText("");
    store.setValue("");
    store.setDropdownOpen(true);
    store.setKeyboardIndex(0);
    return;
  }

  if (isPromptField && !versionStep) {
    const promptId = Number(value.value);
    const promptName = value.prompt_name ?? value.value;
    store.selectPrompt(promptId, promptName);
    return;
  }

  if (isTraceVersionedEntityField && !versionStep) {
    const entityId = Number(value.value);
    const entityName = value.label ?? value.prompt_name ?? value.value;
    store.selectVersionedEntity(entityId, entityName);
    return;
  }

  if (versionStep) {
    if (value.value === ANY_VERSION_VALUE) {
      if (isPromptField) {
        store.selectPromptAnyVersion();
      } else {
        store.selectVersionedEntityAnyVersion();
      }
      return;
    }

    const versionNumber = Number(value.value);
    const config = getVersionedEntityConfig(store.schema, store.inProgress.field);

    if (isPromptField) {
      const promptName = store.inProgress.promptName ?? "";
      const displayLabel = `${promptName} v${versionNumber}`;

      if (
        (store.inProgress.operator === "in" || store.inProgress.operator === "not_in") &&
        store.inProgress.promptId !== undefined
      ) {
        store.addPromptMultiValue(
          {
            prompt_id: store.inProgress.promptId,
            prompt_version_number: versionNumber,
          },
          displayLabel,
        );
        return;
      }

      store.setValue(
        JSON.stringify({
          prompt_id: store.inProgress.promptId,
          prompt_version_number: versionNumber,
        }),
        displayLabel,
      );
      store.completeFilter();
      return;
    }

    if (isTraceVersionedEntityField && config && store.inProgress.entityId !== undefined) {
      const entityName = store.inProgress.entityName ?? "";
      const displayLabel = formatVersionedEntityDisplayLabel(
        config.kind,
        entityName,
        versionNumber,
      );
      const entityValue = createVersionedEntityFilterValue(
        config.kind,
        store.inProgress.entityId,
        versionNumber,
      );

      if (store.inProgress.operator === "in" || store.inProgress.operator === "not_in") {
        store.addVersionedEntityMultiValue(entityValue, displayLabel);
        return;
      }

      store.setValue(JSON.stringify(entityValue), displayLabel);
      store.completeFilter();
      return;
    }

    return;
  }

  if (store.isMultiSelectMode) {
    if (store.multiValues.includes(value.value)) {
      store.removeMultiValue(value.value);
    } else {
      const multiDisplay =
        value.label ?? (isPromptField ? (value.prompt_name ?? value.value) : undefined);
      store.addMultiValue(value.value, multiDisplay);
    }
    return;
  }

  store.setValue(value.value, value.label);
  store.completeFilter();
};
